import {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import {
    View,
    StyleSheet,
    Platform,
} from 'react-native';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {useOrganizationData} from '@/data/app-data-hooks';
import {AppDataMediaFolder, AppDataMediaItem} from '@/data/app-data-interfaces';
import {ThemeType} from '@/theming/baseTheme';
import {useRouter, useLocalSearchParams} from 'expo-router';
import {appDataKeys} from '@/data/app-data-keys';
import {useOrganizationNavigation} from '@/hooks/useOrganizationNavigation';
import {useHardwareBack} from '@/hooks/useHardwareBack';
import {HeaderText} from '@/components/common/HeaderText';
import {MediaBreadcrumb} from '@/components/media/media-library/MediaBreadcrumb';
import {MediaGrid} from '@/components/media/media-library/MediaGrid';
import {useCommonStyles} from '@/hooks/useCommonStyles';
import {useIsInSharedFolder} from '@/data/shared-folder-hooks';
import {SharedFolderFAB} from '@/components/media/media-library/SharedFolderFAB';
import {SharedFolderActionSheet} from '@/components/media/media-library/SharedFolderActionSheet';
import {createSubfolder} from '@/data/shared-folder-api';
import {useSharedFolderUpload} from '@/hooks/useSharedFolderUpload';

interface MediaLibraryScreenProps {
    orgId: string;
}

interface FolderWithChildren extends AppDataMediaFolder {
    parentId?: string;
    children?: FolderWithChildren[];
}

export const MediaLibraryScreen = ({orgId}: MediaLibraryScreenProps) => {
    const {t} = useTranslation();
    const router = useRouter();
    const searchParams = useLocalSearchParams();
    const {theme} = useCurrentTheme();
    const commonStyles = useCommonStyles();
    const {data: orgData} = useOrganizationData(orgId);
    const queryClient = useQueryClient();
    const navigation = useOrganizationNavigation(orgId);

    const styles = createStyles(theme);

    // Helper functions for URL state management
    const updateUrlState = useCallback((folderId: string | null, history: string[]) => {
        const params: any = {};
        if (folderId) {
            params.currentFolder = folderId;
        }
        if (history.length > 0) {
            params.navHistory = history.join(',');
        }

        if (Platform.OS === 'web') {
            // On web, use pushState to create browser history entries
            const url = new URL(window.location.href);
            if (folderId) {
                url.searchParams.set('currentFolder', folderId);
            } else {
                url.searchParams.delete('currentFolder');
            }
            if (history.length > 0) {
                url.searchParams.set('navHistory', history.join(','));
            } else {
                url.searchParams.delete('navHistory');
            }
            window.history.pushState(null, '', url.toString());
        } else {
            // On mobile, use setParams to avoid creating unnecessary history
            router.setParams(params);
        }
    }, [router]);

    const findFolderById = useCallback((folderId: string, folderList: FolderWithChildren[]): FolderWithChildren | null => {
        for (const folder of folderList) {
            if (folder.id === folderId) return folder;
            if (folder.children) {
                const found = findFolderById(folderId, folder.children);
                if (found) return found;
            }
        }
        return null;
    }, []);

    const [currentFolder, setCurrentFolder] = useState<FolderWithChildren | null>(null);
    const [navigationHistory, setNavigationHistory] = useState<(FolderWithChildren | null)[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Transform flat folder structure to hierarchical
    const foldersWithHierarchy = useMemo((): FolderWithChildren[] => {
        if (!orgData?.media.folders) return [];

        const folders = (Object.values(orgData.media.folders) as AppDataMediaFolder[]).map(folder => ({
            ...folder,
            parentId: folder.parentId || undefined,
            children: [],
        }));

        // Build hierarchy
        const folderMap = new Map<string, FolderWithChildren>();
        const rootFolders: FolderWithChildren[] = [];

        folders.forEach(folder => {
            folderMap.set(folder.id, folder);
        });

        folders.forEach(folder => {
            if (folder.parentId) {
                const parent = folderMap.get(folder.parentId);
                if (parent) {
                    parent.children = parent.children || [];
                    parent.children.push(folder);
                }
            } else {
                rootFolders.push(folder);
            }
        });

        // Sort by sorting property
        const sortFolders = (folders: FolderWithChildren[]) => {
            folders.sort((a, b) => a.sorting - b.sorting);
            folders.forEach(folder => {
                if (folder.children) {
                    sortFolders(folder.children);
                }
            });
        };

        sortFolders(rootFolders);
        return rootFolders;
    }, [orgData?.media.folders]);

    // Guard so URL restoration only runs once on initial mount
    const hasInitializedFromUrl = useRef(false);

    // Initialize state from URL when folders are loaded
    useEffect(() => {
        if (hasInitializedFromUrl.current) return;
        if (foldersWithHierarchy.length === 0) return;

        // On web, read directly from window.location because pushState
        // doesn't update Expo Router's searchParams
        let currentFolderId: string | undefined;
        let historyParam: string | undefined;

        if (Platform.OS === 'web') {
            const urlParams = new URLSearchParams(window.location.search);
            currentFolderId = urlParams.get('currentFolder') || undefined;
            historyParam = urlParams.get('navHistory') || undefined;
        } else {
            currentFolderId = searchParams.currentFolder as string;
            historyParam = searchParams.navHistory as string;
        }

        let urlCurrentFolder: FolderWithChildren | null = null;
        let urlNavHistory: (FolderWithChildren | null)[] = [];

        if (currentFolderId) {
            urlCurrentFolder = findFolderById(currentFolderId, foldersWithHierarchy);
        }

        if (historyParam) {
            const historyIds = historyParam.split(',');
            urlNavHistory = historyIds.map(id =>
                id === 'null' ? null : findFolderById(id, foldersWithHierarchy)
            ).filter(item => item !== undefined) as (FolderWithChildren | null)[];
        }

        setCurrentFolder(urlCurrentFolder);
        setNavigationHistory(urlNavHistory);
        hasInitializedFromUrl.current = true;
    }, [foldersWithHierarchy, findFolderById, searchParams.currentFolder, searchParams.navHistory]);

    // Get current level folders
    const currentLevelFolders = useMemo(() => {
        if (!currentFolder) return foldersWithHierarchy;
        return currentFolder.children || [];
    }, [currentFolder, foldersWithHierarchy]);

    // Get media items in current folder
    const currentMediaItems = useMemo(() => {
        if (!orgData?.media.items) return [];

        const folderId = currentFolder?.id || null;
        const allItems = Object.values(orgData.media.items);

        const filtered = allItems.filter(item => {
            // Match items in root folder (folderId is null, undefined, or empty string)
            if (!folderId) {
                return !item.folderId || item.folderId === '';
            }
            // Match items in specific folder
            return item.folderId === folderId;
        });

        return filtered.sort((a, b) => a.sorting - b.sorting);
    }, [orgData?.media.items, currentFolder]);

    // Get total items count recursively
    const getTotalItemsCount = useCallback((folder: FolderWithChildren): number => {
        if (!orgData?.media.items) return 0;

        let count = Object.values(orgData.media.items)
            .filter(item => item.folderId === folder.id).length;

        if (folder.children) {
            folder.children.forEach(child => {
                count += getTotalItemsCount(child);
            });
        }

        return count;
    }, [orgData?.media.items]);

    // Get breadcrumb path
    const breadcrumbPath = useMemo(() => {
        if (!currentFolder) return [];

        const path: FolderWithChildren[] = [];
        let current: FolderWithChildren | null = currentFolder;

        while (current) {
            path.unshift(current);
            const parentId: string | undefined = current.parentId;
            if (parentId) {
                current = foldersWithHierarchy.find(f => f.id === parentId) || null;
                if (!current) {
                    // Search in nested folders
                    const findInNested = (folders: FolderWithChildren[]): FolderWithChildren | null => {
                        for (const folder of folders) {
                            if (folder.id === parentId) return folder;
                            if (folder.children) {
                                const found = findInNested(folder.children);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    current = findInNested(foldersWithHierarchy);
                }
            } else {
                current = null;
            }
        }

        return path;
    }, [currentFolder, foldersWithHierarchy]);

    // Navigation handlers
    const handleFolderPress = useCallback((folder: FolderWithChildren) => {
        const newHistory = [...navigationHistory, currentFolder];
        setNavigationHistory(newHistory);
        setCurrentFolder(folder);

        // Update URL state
        const historyIds = newHistory.map(f => f?.id || 'null');
        updateUrlState(folder.id, historyIds);
    }, [currentFolder, navigationHistory, updateUrlState]);

    const handleBreadcrumbNavigate = useCallback((folder: FolderWithChildren | null) => {
        if (folder === currentFolder) return;

        setCurrentFolder(folder);
        setNavigationHistory([]); // Clear history when using breadcrumb

        // Update URL state
        updateUrlState(folder?.id || null, []);
    }, [currentFolder, updateUrlState]);

    const handleMediaPress = useCallback((item: AppDataMediaItem) => {
        // Navigate to media player/viewer
        const currentFolderId = currentFolder?.id || '';
        navigation.navigateToVideo(item.id, 'folder', currentFolderId);
    }, [navigation, currentFolder]);

    const handleBackPress = useCallback(() => {
        if (navigationHistory.length > 0) {
            const previous = navigationHistory[navigationHistory.length - 1];
            const newHistory = navigationHistory.slice(0, -1);
            setNavigationHistory(newHistory);
            setCurrentFolder(previous);

            // Update URL state
            const historyIds = newHistory.map(f => f?.id || 'null');
            updateUrlState(previous?.id || null, historyIds);
            return true;
        } else if (currentFolder) {
            const parentId = currentFolder.parentId;
            if (parentId) {
                const parent = foldersWithHierarchy.find(f => f.id === parentId);
                setCurrentFolder(parent || null);
                updateUrlState(parent?.id || null, []);
            } else {
                setCurrentFolder(null);
                updateUrlState(null, []);
            }
            return true;
        }
        return false;
    }, [navigationHistory, currentFolder, foldersWithHierarchy, updateUrlState]);

    const handleHeaderBackPress = useCallback(() => {
        navigation.navigateBack();
    }, [navigation]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await queryClient.invalidateQueries({queryKey: appDataKeys.all});
        } finally {
            setIsRefreshing(false);
        }
    }, [queryClient]);

    // Shared folder state
    const isInShared = useIsInSharedFolder(orgId, currentFolder?.id);

    const handleCreateFolder = useCallback(async (name: string) => {
        if (!currentFolder?.id) return;
        await createSubfolder(currentFolder.id, name, orgId);
        await queryClient.invalidateQueries({queryKey: appDataKeys.all});
    }, [currentFolder, orgId, queryClient]);

    const {pickAndUpload} = useSharedFolderUpload(orgId, currentFolder?.id);

    const [actionSheetVisible, setActionSheetVisible] = useState(false);

    // Handle hardware back button
    useHardwareBack(handleBackPress);

    return (
        <View style={commonStyles.screenContainer}>
            <HeaderText
                title={t('media.screens.library')}
                onBackPress={handleHeaderBackPress}
            />

            <MediaBreadcrumb
                path={breadcrumbPath}
                onNavigate={handleBreadcrumbNavigate}
            />

            <MediaGrid
                folders={currentLevelFolders}
                mediaItems={currentMediaItems}
                onFolderPress={handleFolderPress}
                onMediaPress={handleMediaPress}
                getFolderItemCount={getTotalItemsCount}
                isRefreshing={isRefreshing}
                onRefresh={handleRefresh}
                scrollEnabled={true}
                style={styles.contentContainer}
            />

            {isInShared && (
                <SharedFolderFAB onPress={() => setActionSheetVisible(true)} />
            )}

            <SharedFolderActionSheet
                visible={actionSheetVisible}
                onClose={() => setActionSheetVisible(false)}
                onUpload={pickAndUpload}
                onCreateFolder={handleCreateFolder}
            />
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    contentContainer: {
        flex: 1,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md
    },
});
